/**
 * WHERE on the guide page the reader was standing when they chose to try a lab.
 *
 * This is a different question from `entry` in `opening-answer.ts`, and both are needed. `entry` says
 * HOW they came in (they answered the in-page question, or they clicked a plain link). `placement`
 * says WHICH CTA sent them. A reader can arrive by plain link from the article block or by plain link
 * from the newsletter confirmation, and those two are worth very different things, so one property
 * cannot carry both.
 *
 * 🔴 WHY THIS EXISTS AT ALL. Bifei's Sep 6-7 page test read 90 / 39 / 1 newsletter captures across
 * three layouts: putting the Labs block in the guide page's TOP CTA slot costs 57-99% of newsletter
 * signups, because the two asks are rivals for the same slot and the newsletter wins on volume by
 * roughly ninety to one. The newsletter CONFIRMATION state is the one place on the page where the two
 * are not rivals: the email is already captured, the screen is otherwise a dead end, and a link there
 * takes nothing away from anyone. That placement is only defensible if we can read it SEPARATELY from
 * the article block, otherwise a win in one slot and a loss in the other cancel invisibly and the
 * board reports the average of two opposite effects.
 *
 * It is a PROPERTY on every existing `labs_preview_*` event rather than a new event name on purpose. A
 * new event would count arrivals and nothing else; a property splits every step of the funnel, so
 * "does the newsletter path finish, and does it convert" is one breakdown instead of a new funnel.
 */

/**
 * The CTA slots we can tell apart.
 *
 * Deliberately a closed set, not free text. The value comes off a query string a stranger can edit,
 * lands in Mixpanel as a breakdown dimension, and a typo or an injected value would fork a row off
 * every card on the board. Unknown input resolves to `ARTICLE`, which is where all traffic came from
 * before this existed, so a mangled link degrades to the truthful default rather than to a mystery
 * bucket.
 */
export const PREVIEW_PLACEMENT = {
  /** The question block inside the article body. The original placement, and the control. */
  ARTICLE: 'article',
  /** The "You are in!" newsletter confirmation state, after the email is already captured. */
  NEWSLETTER: 'newsletter',
  /**
   * The lab running INSIDE the guide page, in a frame, instead of on its own route.
   *
   * A different value from `ARTICLE` even though it occupies the same slot, because the two are not
   * comparable populations. An article arrival chose to leave the page; an embed arrival chose to
   * start something without leaving. If they shared a value we could never answer whether removing
   * the navigation was what helped.
   */
  EMBED: 'embed',
} as const;

export type PreviewPlacement = (typeof PREVIEW_PLACEMENT)[keyof typeof PREVIEW_PLACEMENT];

/** The query parameter the newsletter-confirmation link carries. */
export const PLACEMENT_PARAM = 'via';

/**
 * The accepted `?via=` values.
 *
 * `article` is accepted as well as implied so a future A/B can tag both arms explicitly rather than
 * one arm being "the one without a parameter", which is the shape that makes a broken link look like
 * a control.
 */
const PLACEMENTS: Readonly<Record<string, PreviewPlacement>> = {
  article: PREVIEW_PLACEMENT.ARTICLE,
  embed: PREVIEW_PLACEMENT.EMBED,
  newsletter: PREVIEW_PLACEMENT.NEWSLETTER,
};

/** Reduce an untrusted `?via=` to a known placement. Anything unrecognised is the article default. */
export function normalisePlacement(raw: string | string[] | undefined): PreviewPlacement {
  // An array means the param repeated (`?via=article&via=newsletter`). First wins, matching `?src=`
  // and `?a1=`, so all three params disagree the same way rather than each inventing a rule.
  const value = Array.isArray(raw) ? raw[0] : raw;

  if (!value) {
    return PREVIEW_PLACEMENT.ARTICLE;
  }

  const key = value.trim().toLowerCase();

  // 🔴 `Object.hasOwn`, NOT a bare lookup with `??`. `PLACEMENTS['__proto__']` resolves up the
  // prototype chain to `Object.prototype`, which is an object and therefore truthy, so `??` never
  // fires and the placement becomes an object: it lands in Mixpanel as a garbage breakdown value and
  // defeats the closed set this map exists to be. Caught by `entry-placement.spec.ts`, which is why
  // that spec tests `__proto__`, `constructor` and `toString` by name rather than trusting the shape.
  const known = Object.hasOwn(PLACEMENTS, key) ? PLACEMENTS[key] : undefined;

  return known ?? PREVIEW_PLACEMENT.ARTICLE;
}

/** Read and normalise `?via=` from the browser's own URL. */
export function readPlacementFromLocation(): PreviewPlacement {
  if (typeof window === 'undefined') {
    return PREVIEW_PLACEMENT.ARTICLE;
  }

  return normalisePlacement(new URLSearchParams(window.location.search).get(PLACEMENT_PARAM) ?? undefined);
}

/**
 * Whether this reader has ALREADY given us their email somewhere else, so the payoff must not ask.
 *
 * True only for the newsletter confirmation, where the Webflow form has just captured the address and
 * handed it to marketing. Asking a second time on the same page visit is the kind of friction that
 * reads as a company that does not talk to itself.
 *
 * 🔴 THE ADDRESS IS NOT AND MUST NOT BE IN THE URL. Webflow's confirmation block is static HTML, so
 * the only way to pass the typed address would be a script rewriting the href, which would put a
 * student's email into a query string, browser history, referrer headers and every analytics log it
 * touches. We therefore know THAT they subscribed and never WHO, which is exactly enough: the address
 * already lives in Webflow and Customer.io, and the lab's copy of it would be a second store of
 * personal data for no gain (see `submit-preview-lead.ts` on why that store is deliberately thin).
 *
 * 🔴 THIS IS FORGEABLE, AND THAT IS AN ACCEPTED TRADE, NOT AN OVERSIGHT. Anyone can append
 * `?via=newsletter` and reach their readout without leaving an address. What that costs is one email
 * we might have captured; what it cannot do is expose anyone else's data, because the readout is built
 * entirely from the swipes the reader just made in their own browser. A signed token would close it,
 * and cannot be minted by a static Webflow page. If the forged share of `placement=newsletter` ever
 * looks material, cross-check it against `guide_source`: a real newsletter arrival always carries the
 * slug of a page that has the block, and a shared link tends to lose it.
 */
export function isEmailAlreadyCaptured(placement: PreviewPlacement): boolean {
  return placement === PREVIEW_PLACEMENT.NEWSLETTER;
}

/**
 * Whether the lab is running inside a frame on the guide page rather than on its own route.
 *
 * Derived from the SAME `?via=` parameter rather than a second `?embed=1`, because the two would
 * always have to agree and any URL where they disagreed would be a bug with no right answer.
 *
 * Three things change when this is true, and each of them is a real defect if it does not:
 *   1. The app header is hidden. The frame sits inside a page that already shows Extern's header, so
 *      rendering a second one makes the article look broken, and its Sign in link would load the
 *      sign-in page INSIDE the article.
 *   2. Outbound navigation targets the TOP window. A sign-up handoff that loads in the frame traps
 *      the reader in a window sized for the modal, not for a full-page form.
 *   3. The lab opens on the first question rather than the welcome screen, because the block on the
 *      guide page has already made that pitch.
 *
 * It used to change a fourth: the page reported its height so the frame could grow. The modal gives
 * the lab a real viewport, so there is no height to report and nothing listening for one.
 */
export function isEmbedded(placement: PreviewPlacement): boolean {
  return placement === PREVIEW_PLACEMENT.EMBED;
}
